import { Filter, Check } from 'lucide-react';
import { forwardRef, HTMLAttributes, useState, useRef, useEffect } from 'react';
import { cn } from '@/shared/lib/utils';
import { BadgeVariant } from '@/shared/types/badge.types';
import { ButtonVariant, ButtonSize } from '@/shared/types/button.types';
import { Badge } from './badge';
import { Button } from './button';
import { Checkbox } from './checkbox';
import { Label } from './label';
import { SearchInput } from './search-input';

export interface FilterOption {
  readonly value: string;
  readonly label: string;
  readonly count?: number | undefined;
}

export interface FilterDropdownProps extends Omit<
  HTMLAttributes<HTMLDivElement>,
  'onChange'
> {
  readonly label: string;
  readonly options: readonly FilterOption[];
  readonly value: readonly string[];
  readonly onChange: (value: readonly string[]) => void;
  readonly onClear?: (() => void) | undefined;
  readonly placeholder?: string | undefined;
  readonly searchable?: boolean | undefined;
  readonly showCount?: boolean | undefined;
  readonly maxHeight?: string | undefined;
  readonly disabled?: boolean | undefined;
}

/**
 * FilterDropdown component for multi-select filtering
 *
 * Features:
 * - Multi-select with checkboxes
 * - Search within options
 * - Clear all button
 * - Active filter count badge
 * - Item counts
 * - Click outside to close
 * - WCAG 2.1 AA compliant
 *
 * @example
 * ```tsx
 * <FilterDropdown
 *   label="Status"
 *   options={[
 *     { value: 'active', label: 'Active', count: 42 },
 *     { value: 'pending', label: 'Pending', count: 15 }
 *   ]}
 *   value={selectedStatuses}
 *   onChange={setSelectedStatuses}
 *   searchable
 * />
 * ```
 */
export const FilterDropdown = forwardRef<HTMLDivElement, FilterDropdownProps>(
  (
    {
      className,
      label,
      options,
      value,
      onChange,
      onClear,
      placeholder = 'Search filters...',
      searchable = true,
      showCount = true,
      maxHeight = '300px',
      disabled = false,
      ...props
    },
    _ref
  ) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const containerRef = useRef<HTMLDivElement>(null);

    // Close dropdown when clicking outside
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          containerRef.current &&
          !containerRef.current.contains(event.target as Node)
        ) {
          setIsOpen(false);
        }
      };

      if (isOpen) {
        document.addEventListener('mousedown', handleClickOutside);
        return () =>
          document.removeEventListener('mousedown', handleClickOutside);
      }

      return undefined;
    }, [isOpen]);

    // Filter options based on search query
    const filteredOptions = searchQuery
      ? options.filter((option) =>
          option.label.toLowerCase().includes(searchQuery.toLowerCase())
        )
      : options;

    const activeCount = value.length;

    const handleToggle = () => {
      if (!disabled) {
        setIsOpen(!isOpen);
      }
    };

    const handleOptionToggle = (optionValue: string) => {
      const newValue = value.includes(optionValue)
        ? value.filter((v) => v !== optionValue)
        : [...value, optionValue];
      onChange(newValue);
    };

    const handleClearAll = () => {
      onChange([]);
      onClear?.();
    };

    const handleSelectAll = () => {
      onChange(filteredOptions.map((opt) => opt.value));
    };

    return (
      <div
        ref={containerRef}
        className={cn('relative inline-block', className)}
        {...props}
      >
        {/* Trigger button */}
        <Button
          type="button"
          variant={ButtonVariant.Outline}
          onClick={handleToggle}
          disabled={disabled}
          className={cn(
            'h-11 min-h-[44px] gap-2',
            activeCount > 0 && 'border-primary'
          )}
          aria-expanded={isOpen}
          aria-haspopup="menu"
          aria-label={`Filter by ${label}`}
        >
          <Filter className="size-4" aria-hidden="true" />
          <span>{label}</span>
          {activeCount > 0 && (
            <Badge
              variant={BadgeVariant.Default}
              className="ml-1 h-5 min-w-[20px] px-1"
            >
              {activeCount}
            </Badge>
          )}
        </Button>

        {/* Dropdown menu */}
        {isOpen && (
          <div
            className={cn(
              'absolute z-50 mt-2 w-72 rounded-md border border-border bg-popover shadow-lg',
              'animate-in fade-in-0 zoom-in-95'
            )}
            role="menu"
            aria-label={`${label} filter options`}
          >
            {/* Header */}
            <div className="border-b border-border p-3">
              <div className="mb-3 flex items-center justify-between">
                <h3 className="text-sm font-medium text-foreground">{label}</h3>
                {activeCount > 0 && (
                  <Button
                    type="button"
                    variant={ButtonVariant.Ghost}
                    size={ButtonSize.Small}
                    onClick={handleClearAll}
                    className="h-auto p-0 text-xs hover:bg-transparent hover:text-destructive"
                  >
                    Clear all
                  </Button>
                )}
              </div>

              {/* Search input */}
              {searchable && (
                <SearchInput
                  placeholder={placeholder}
                  onSearch={setSearchQuery}
                  debounceMs={200}
                  showClearButton
                  className="w-full"
                />
              )}
            </div>

            {/* Options list */}
            <div
              className="overflow-y-auto p-2"
              style={{ maxHeight }}
              role="group"
              aria-label="Filter options"
            >
              {filteredOptions.length === 0 ? (
                <div className="p-4 text-center text-sm text-muted-foreground">
                  No options found
                </div>
              ) : (
                <>
                  {/* Select all option */}
                  {filteredOptions.length > 1 && (
                    <>
                      <div className="flex items-center gap-3 rounded-md px-3 py-2.5 transition-colors hover:bg-accent">
                        <Checkbox
                          id={`filter-${label}-select-all`}
                          checked={
                            filteredOptions.length > 0 &&
                            filteredOptions.every((opt) =>
                              value.includes(opt.value)
                            )
                          }
                          onCheckedChange={handleSelectAll}
                          className="flex-shrink-0"
                        />
                        <Label
                          htmlFor={`filter-${label}-select-all`}
                          className="flex flex-1 cursor-pointer text-sm font-medium"
                        >
                          Select all
                        </Label>
                      </div>
                      <div className="my-1 h-px bg-border" />
                    </>
                  )}

                  {/* Individual options */}
                  {filteredOptions.map((option) => {
                    const isSelected = value.includes(option.value);
                    return (
                      <div
                        key={option.value}
                        className="flex items-center gap-3 rounded-md px-3 py-2.5 transition-colors hover:bg-accent"
                      >
                        <Checkbox
                          id={`filter-${label}-${option.value}`}
                          checked={isSelected}
                          onCheckedChange={() =>
                            handleOptionToggle(option.value)
                          }
                          className="flex-shrink-0"
                        />
                        <Label
                          htmlFor={`filter-${label}-${option.value}`}
                          className="flex flex-1 cursor-pointer items-center justify-between text-sm"
                        >
                          <span className="text-foreground">
                            {option.label}
                          </span>
                          {showCount && option.count !== undefined && (
                            <span className="text-xs text-muted-foreground">
                              ({option.count})
                            </span>
                          )}
                        </Label>
                        {isSelected && (
                          <Check
                            className="size-4 flex-shrink-0 text-primary"
                            aria-hidden="true"
                          />
                        )}
                      </div>
                    );
                  })}
                </>
              )}
            </div>

            {/* Footer with action buttons */}
            {activeCount > 0 && (
              <div className="flex items-center justify-between border-t border-border p-3">
                <span className="text-xs text-muted-foreground">
                  {activeCount} selected
                </span>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant={ButtonVariant.Ghost}
                    size={ButtonSize.Small}
                    onClick={() => setIsOpen(false)}
                    className="h-8"
                  >
                    Close
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }
);

FilterDropdown.displayName = 'FilterDropdown';
